/**
 * Pure tree logic behind <CheckboxTree/>: grouping rows by successive keys and
 * computing checked/indeterminate state.
 */

export interface TreeLeaf<Row> {
  kind: 'leaf';
  key: string;
  row: Row;
}

export interface TreeBranch<Row> {
  kind: 'branch';
  key: string;
  label: string;
  children: TreeNode<Row>[];
}

export type TreeNode<Row> = TreeLeaf<Row> | TreeBranch<Row>;

// Group labels: null and undefined are stringified ("null", "undefined") and
// only the empty string is replaced by N/A (see migration.md).
const groupValue = (value: unknown): string =>
  value === '' ? 'N/A' : String(value);

/**
 * Groups `rows` by the successive `breakdown` keys, e.g. ['region', 'country'],
 * producing branches (region -> country) whose leaves are the rows.
 * Group order follows first appearance, like lodash.groupBy did.
 */
export const buildTree = <Row extends Record<string, unknown>>(
  rows: readonly Row[],
  breakdown: readonly (keyof Row & string)[],
  idKey: keyof Row & string,
  pathPrefix = '',
): TreeNode<Row>[] => {
  if (breakdown.length === 0) {
    return rows.map((row) => ({
      kind: 'leaf',
      key: `${pathPrefix}${String(row[idKey])}`,
      row,
    }));
  }
  const [first, ...rest] = breakdown as [
    keyof Row & string,
    ...(keyof Row & string)[],
  ];
  const groups = new Map<string, Row[]>();
  rows.forEach((row) => {
    const label = groupValue(row[first]);
    const bucket = groups.get(label);
    if (bucket) bucket.push(row);
    else groups.set(label, [row]);
  });
  return Array.from(groups, ([label, groupRows]) => {
    const key = `${pathPrefix}${label}/`;
    return {
      kind: 'branch',
      key,
      label,
      children: buildTree(groupRows, rest, idKey, key),
    };
  });
};

export const collectLeaves = <Row>(node: TreeNode<Row>): Row[] =>
  node.kind === 'leaf' ? [node.row] : node.children.flatMap(collectLeaves);

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

export const branchState = <Row>(
  node: TreeBranch<Row>,
  isSelected: (row: Row) => boolean,
): CheckState => {
  const leaves = collectLeaves(node);
  if (leaves.length === 0) return 'unchecked';
  const selectedCount = leaves.filter(isSelected).length;
  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === leaves.length) return 'checked';
  return 'indeterminate';
};
